// CSpell:ignore articleid amphtml
import { findFirstNonEmptyString } from "@product/scmp-sdk";
import { useAtomValue } from "jotai";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useEffect } from "react";
import { graphql, useFragment } from "react-relay";

import { PageSeo, ViewportTag } from "~/components/meta";
import { useKeywordMetaTags } from "~/components/meta/hooks";
import { trackingAtom } from "~/components/tracking";
import { config } from "~/data";
import { formatInTimeZone } from "~/lib/utils";
import type { articleSeoArticle$key } from "~/queries/__generated__/articleSeoArticle.graphql";

import { ExcludedArticleIds } from "./consts";
import { useArticleSeoHelper } from "./hooks";

type Props = {
  reference: articleSeoArticle$key;
};

export const ArticleSeo: FunctionComponent<Props> = ({ reference: reference_ }) => {
  const article = useFragment(
    graphql`
      fragment articleSeoArticle on Article
      @argumentDefinitions(customContents: { type: "[CustomContentInput]", defaultValue: [] }) {
        ...hooksArticleSeoArticle
        entityId
        publishedDate
        updatedDate
        headline
        summary {
          text
        }
        subHeadline {
          text
        }
        body(customContents: $customContents) {
          text
        }
        keywords
        sections {
          value {
            name
          }
        }
        topics {
          name
          entityId
        }
        urlAlias
        authors {
          name
        }
        images {
          style(filter: { style: "768x768" }) {
            url
          }
        }
        copyrighted
        entityUuid
        socialHeadline
        sponsor
      }
    `,
    reference_,
  );

  const { computedMetaHeadline, isAmpMetaTagRequired, openGraphMetaData } =
    useArticleSeoHelper(article);

  const pickedSummary = first(article.summary?.text?.split("\n"));
  const pickedSubHeadline = first(article.subHeadline?.text?.split("\n"));
  const pickedBody = first(article.body?.text?.split("\n"));

  const sanitizedDescription = findFirstNonEmptyString(
    pickedSummary,
    pickedSubHeadline,
    pickedBody,
  );

  const sanitizedKeywords = article.keywords?.join(",") ?? "";

  const keywordTags = useKeywordMetaTags(
    sanitizedKeywords.length > 0 ? sanitizedKeywords : undefined,
  );

  const publishDate = article.publishedDate
    ? /* Cspell: disable-next-line */
      formatInTimeZone(article.publishedDate, "yyyy-MM-dd'T'HH:mm:ssXXX")
    : "";

  const updatedDate = article.updatedDate
    ? /* Cspell: disable-next-line */
      formatInTimeZone(article.updatedDate, "yyyy-MM-dd'T'HH:mm:ssXXX")
    : publishDate;

  const dates = [
    {
      content: publishDate,
      property: "article:published_time",
    },
    {
      content: updatedDate,
      property: "article:modified_time",
    },
  ];

  const sanitizedSection =
    article.sections
      ?.flatMap(section => section?.value)
      .map((section, index) => ({
        content: section?.name ?? "",
        keyOverride: `tag${index}`,
        property: "article:section",
      })) ?? [];

  const sanitizedTags =
    article.topics?.map((topic, index) => ({
      content: topic?.name ?? "",
      keyOverride: `tag-${index}`,
      property: "article:tag",
    })) ?? [];

  const otherMetaTags = [
    {
      content: "max-image-preview:large",
      keyOverride: "robot-1",
      name: "robots",
    },
    {
      content: "25fd909c3fd10ded451415fc076ba8",
      name: "pocket-site-verification",
    },
    {
      content: "#ffffff",
      name: "theme-color",
    },
    {
      content: "yes",
      name: "mobile-web-app-capable",
    },
    ViewportTag,
  ];

  const cXenseMetaTags = [
    {
      content: article.entityId,
      name: "cXenseParse:articleid",
    },
    {
      content: first(article.authors)?.name ?? "",
      name: "cXenseParse:author",
    },
    {
      content: first(article.images)?.style?.url ?? "",
      property: "cXenseParse:image",
    },
  ];

  const mobileContentArticleUrl = `scmp:/${article.urlAlias}?entity=node&type=article&id=${article.entityId}`;

  const mobileAppMetaData = [
    {
      content: mobileContentArticleUrl,
      property: "al:ios:url",
    },
    {
      content: mobileContentArticleUrl,
      property: "al:android:url",
    },
  ];

  const isRobotsNoIndex = ExcludedArticleIds.includes(article.entityId);

  const linkTags = [
    {
      href: `${config.general.siteUrl}${article.urlAlias}`,
      rel: "canonical",
    },
  ];

  if (isAmpMetaTagRequired) {
    linkTags.push({
      href: `https://amp.scmp.com${article.urlAlias}`,
      rel: "amphtml",
    });
  }

  const { trackPageView } = useAtomValue(trackingAtom);

  useEffect(() => {
    const {
      authors,
      copyrighted,
      entityId,
      entityUuid,
      headline,
      publishedDate,
      sections,
      socialHeadline,
      sponsor,
      topics,
      urlAlias,
    } = article;

    const sanitizedAuthors = authors?.map(author => author?.name).join(", ");

    const joinedSectionNames = sections?.[0]?.value?.map(section => section?.name).join(", ");

    const sanitizedTopicData = topics?.reduce<{ id: string[]; name: string[] }>(
      (topicData, topic) => {
        if (topic?.entityId) topicData.id.push(topic.entityId);
        if (topic?.name) topicData.name.push(topic.name);
        return topicData;
      },
      {
        id: [],
        name: [],
      },
    );

    trackPageView({
      articleID: entityId,
      authorName: first(authors)?.name ?? "",
      authors: sanitizedAuthors,
      contentType: "Article",
      entityId,
      entityUuid,
      page: "Article",
      pageTitle: headline,
      path: urlAlias,
      primarySection: first(sections?.[0]?.value)?.name ?? "",
      publishedDate: publishedDate ? formatInTimeZone(publishedDate, "dd MMM yyyy") : null,
      scmpCopyright: copyrighted ? "1" : "0",
      sections: joinedSectionNames,
      socialHeadline,
      sponsor: sponsor ?? "",
      topicID: sanitizedTopicData?.id.join(", ") ?? "",
      topics: sanitizedTopicData?.name.join(", ") ?? "",
      webHeadline: headline ?? "",
    });
  }, [trackPageView, article]);

  return (
    <PageSeo
      additionalLinkTags={[...linkTags]}
      additionalMetaTags={[
        ...keywordTags,
        ...sanitizedSection,
        ...sanitizedTags,
        ...dates,
        ...otherMetaTags,
        ...cXenseMetaTags,
        ...mobileAppMetaData,
      ]}
      description={sanitizedDescription}
      noindex={isRobotsNoIndex}
      openGraph={{
        ...openGraphMetaData,
      }}
      title={computedMetaHeadline}
    />
  );
};

ArticleSeo.displayName = "ArticleSeo";
